import * as React from 'react';
import {
  AGENT_LAN,
  SelectionOption,
  SELECTION_KEY,
  getSelectionOptions,
  N_AGENT_LANGUAGE_UPDATE,
  API,
  COPILOT_ACTION_KEYS
} from '@config/index';
import {
  UserContext,
  UserContextProps,
  UserProps,
  OrgContextProps,
  OrgInfo,
  AgentInfo,
  AgentContextProps,
  AgentContext,
  OrgContext
} from '@interface/index';
import {
  NiSpin,
  NiSelect,
  VersionButton,
  GetVersionStatus,
  niConfirm
} from '@component/index';
import { isEqual, getAgentId, isProviderAgent, setCurrentLanguageOptions, getAgentPathPrefix, getMasterAgentName, getAgentLangFromLangLabel, isSingleLanguageAgent, getAgentInfo, instance, isStructAgent } from '@lib/index';

import './sub-nav.less';
import { localizedString } from '@locale/locale';
import { message } from 'antd';

export interface SubNavPropsInfo {
  user: UserProps;
  org: OrgInfo;
  agent?: AgentInfo;
  history: any;
}

export interface SubNavStateInfo {
  loading: boolean;
  structId?: string;
  langAgentOptions?: SelectionOption[];
}

const defaultState: SubNavStateInfo = {
  loading: false
};


class SubNavComp extends React.Component<SubNavPropsInfo, SubNavStateInfo> {
  public state: SubNavStateInfo = { ...defaultState };

  listen: any;
  actionListener: any;
  componentDidMount() {
    this.onInit();
    this.actionListener = instance.on(COPILOT_ACTION_KEYS.SWITCH_VIEW, (args: any) => {
      if (!args.view) {
        message.warning('param view is required');
        return;
      }

      this.selectLang(args.view);
    });
  }

  componentWillUnmount(): void {
    this.actionListener();
  }

  onInit = async () => {
    const { agent, user } = this.props;

    if (!getAgentId()) {
      return;
    }

    this.setState({
      loading: true
    });

    let structId: string | undefined;

    if (agent) {
      if (agent.language === AGENT_LAN.STRUCT) {
        structId = agent.id;
      } else if (agent.structKey) {
        structId = agent.structKey.agentId;
      }

    }

    const langAgentOptions: any = structId &&
      await getSelectionOptions(
        SELECTION_KEY.LANGUAGE_AGENT_OF_STRUCT,
        [{ value: structId, value_type: 'agent' }]
      );

    setCurrentLanguageOptions(langAgentOptions);

    this.setState({
      structId,
      langAgentOptions,
      loading: false
    });

  }

  render() {
    const { agent, user, history, org } = this.props;
    const {
      loading,
      structId,
      langAgentOptions
    } = this.state;

    return <div className="sub-nav-bar" key={agent && agent.id}>
      <NiSpin spinning={loading}>
        <span className="sub-nav-wrapper">
          {
            <>
              {
                org &&
                agent &&
                // structId &&
                // langAgentOptions &&
                <span>
                  Layer
                  <NiSelect
                    key={structId}
                    // disabled={isProviderAgent() || isSingleLanguageAgent()}
                    className="language-select"
                    value={agent && agent.id}
                    onChange={(value: string, option: any) => {
                      const lang = getAgentLangFromLangLabel(option.children as string);
                      this.selectLang(lang);
                    }}
                  >
                    {
                      (isProviderAgent() || isSingleLanguageAgent()) ?
                        <NiSelect.Option key={agent.id} value={agent.id}>
                          {!isSingleLanguageAgent() ? 'Structure' : `Combined / ${agent.language}`}
                        </NiSelect.Option>
                        : (((langAgentOptions && langAgentOptions.length > 1 && !isStructAgent())
                          ? langAgentOptions.filter(opt => opt.label !== 'Structure')
                          : langAgentOptions) || []).map((opt: SelectionOption) =>
                            <NiSelect.Option key={opt.value} value={opt.value as string}>
                              {opt.label !== 'Structure' ? `${localizedString('agent_lang_prefix', undefined, 'Language')} / ${opt.label && (opt.label as string).toLowerCase()}` : opt.label}
                            </NiSelect.Option>)
                    }
                  </NiSelect>
                </span>
              }
            </>
          }
        </span>
        {agent &&
          <span style={{ paddingTop: 8, height: 40 }}>
            <VersionButton
              key={agent.id}
              history={history}
              agent={agent} />
          </span>
        }
      </NiSpin>
    </div >;
  }

  selectLang = (lang: string) => {
    const { agent } = this.props;

    if (agent && agent.language === lang) {
      return;
    }

    if (agent && agent.language === AGENT_LAN.STRUCT && lang !== agent.language && GetVersionStatus().rebuildingRequired) {
      niConfirm({
        title: 'Confirm',
        icon: null,
        content: localizedString('switch_lang_confirm', undefined, 'The structure view contains unpropagated data. Switching to the language view will automatically propagate the data.'),
        okText: localizedString('propagate'),
        onOk: () => {
          this.setState({ loading: true });
          return API.version.test()
            .then(() => {
              this.switchToLang(lang);
            })
            .catch((e: any) => {
              this.setState({ loading: false });

              if (e.message === 'confirm info' || e.message === '确认信息') {
                niConfirm({
                  width: '60%',
                  title: localizedString('common_error_info'),
                  content: <pre style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all'
                  }}>{e.data}</pre>

                });
              } else {
                message.warning(e.message
                  ? <pre style={{ whiteSpace: 'pre-wrap', textAlign: 'start' }}>{
                    e.message
                  }
                  </pre>
                  : localizedString('common_test_failure'));
              }
              throw e;
            }).finally(() => {
              this.setState({ loading: false });
            });
        }
      });
      return;
    }

    this.switchToLang(lang);
  }

  switchToLang = (lang: string) => {
    const { org, history } = this.props;
    const oldPrefix = getAgentPathPrefix();
    const newPrefix = `/org/${org.name}/agent/${getMasterAgentName()}/${lang}`;
    const currentUrl = document.location;

    const path = currentUrl.pathname.replace(oldPrefix, newPrefix);
    const search = currentUrl.search;
    const hash = currentUrl.hash;

    const everythingAfterHost = path + search + hash;
    history.push(everythingAfterHost);
  }
}


export const SubNav = (props: any) => (
  <UserContext.Consumer>
    {($user: UserContextProps) => (
      <OrgContext.Consumer>
        {($org: OrgContextProps) => (
          <AgentContext.Consumer>
            {($agent: AgentContextProps) => <SubNavComp
              {...props}
              user={$user.user}
              org={$org.org}
              agent={$agent.agent}
            />}
          </AgentContext.Consumer>
        )}
      </OrgContext.Consumer>
    )}
  </UserContext.Consumer>
);
